<script setup lang="ts">
import { computed } from 'vue'
import { useAiChatStore } from '@/stores/ai-chat'
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from '@kyoto2/components/ui/tooltip'

const store = useAiChatStore()

const RADIUS = 6.5
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

const arc = computed(() => (CIRCUMFERENCE * (store.contextPercent ?? 0)) / 100)

/** Status-token color for the fill ring + label (track stays muted). */
const meterColor = computed(() => {
  const pct = store.contextPercent ?? 0
  if (pct >= 90) return 'text-destructive'
  if (pct >= 70) return 'text-amber-600 dark:text-amber-400'
  return 'text-emerald-600 dark:text-emerald-400'
})

function formatTokens(n: number) {
  if (n >= 1000) return `${Math.round(n / 1000)}k`
  return String(n)
}

const detail = computed(() => {
  if (store.contextTokens == null) return ''
  return `${formatTokens(store.contextTokens)} / ${formatTokens(store.contextWindow)} tokens — older history summarizes automatically`
})
</script>

<template>
  <TooltipProvider v-if="store.contextPercent !== null" :delay-duration="200">
    <Tooltip>
      <TooltipTrigger as-child>
        <div
          class="flex items-center gap-1 px-1 text-[11px] tabular-nums select-none"
          :class="meterColor"
        >
          <svg viewBox="0 0 16 16" class="size-3.5 -rotate-90">
            <circle
              cx="8" cy="8" :r="RADIUS"
              fill="none" stroke="currentColor" stroke-width="2.5"
              class="text-muted-foreground/25"
            />
            <circle
              cx="8" cy="8" :r="RADIUS"
              fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
              :stroke-dasharray="`${arc} ${CIRCUMFERENCE}`"
            />
          </svg>
          <span>{{ store.contextPercent }}%</span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top">{{ detail }}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
</template>
